import { useState } from "react";
import { User, Mail, Phone, FileText, Calendar, ArrowLeft, Plus, Shield, Car, KeyRound, Loader2, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";

var AVATAR_COLORS = ["bg-[#3B82F6]", "bg-[#F26A4B]", "bg-amber-500", "bg-purple-500", "bg-emerald-500"];

function getInitials(name) {
  if (!name) return "C";
  return name.split(" ").map(function(n) { return n[0]; }).join("").slice(0, 2).toUpperCase();
}

function formatDate(d) {
  if (!d) return "-";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch(e) { return "-"; }
}

export default function ProfileHeader({ client, onAddConsortium, onAddPolicy }) {
  var navigate = useNavigate();
  var auth = useAuth();
  var isManager = auth.user?.role === "admin" || auth.user?.role === "team_leader";
  var initials = getInitials(client.name);
  var resetState = useState(false);
  var pwdState = useState("");
  var resetLoadingState = useState(false);
  var resetMsgState = useState(null);

  async function handleResetPassword() {
    if (pwdState[0].length < 6) return;
    resetLoadingState[1](true);
    resetMsgState[1](null);
    try {
      var res = await api.post("/clients/" + client.id + "/force-reset-password", { new_password: pwdState[0] });
      resetMsgState[1]({ ok: true, msg: res.data.message });
      pwdState[1]("");
      setTimeout(function() { resetState[1](false); resetMsgState[1](null); }, 2000);
    } catch(err) {
      resetMsgState[1]({ ok: false, msg: err.response?.data?.detail || "Erro ao resetar" });
    } finally { resetLoadingState[1](false); }
  }

  var accountLabel = client.account_type === "pj" ? "PJ" : "PF";

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="profile-header">
      <div className="h-24 bg-gradient-to-r from-[#0A2F3D] to-[#164B60]" />
      <div className="px-4 sm:px-6 pb-5 -mt-10">
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="w-20 h-20 rounded-2xl bg-[#3B82F6] flex items-center justify-center ring-4 ring-white shadow-lg flex-shrink-0">
            <span className="text-white text-2xl font-bold">{initials}</span>
          </div>
          <div className="flex-1 pt-2 sm:pt-0 sm:pb-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-[#0A2F3D]" data-testid="profile-client-name">{client.name}</h1>
              <span className={"px-1.5 py-0.5 text-[10px] font-bold rounded " + (client.account_type === "pj" ? "bg-purple-100 text-purple-700" : "bg-blue-100 text-blue-700")} data-testid="profile-account-type">{accountLabel}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
              {client.email && <span className="flex items-center gap-1 text-xs text-gray-500"><Mail className="w-3 h-3" />{client.email}</span>}
              {client.phone && (
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Phone className="w-3 h-3" />{client.phone}
                  <a href={"https://wa.me/55" + client.phone.replace(/\D/g, "")} target="_blank" rel="noopener noreferrer" className="text-emerald-500 hover:text-emerald-600" data-testid="profile-wpp"><MessageCircle className="w-3.5 h-3.5" /></a>
                </span>
              )}
              {client.cpf && <span className="flex items-center gap-1 text-xs text-gray-500"><FileText className="w-3 h-3" />{client.cpf}</span>}
              {client.cnpj && <span className="flex items-center gap-1 text-xs text-gray-500"><FileText className="w-3 h-3" />{client.cnpj}</span>}
              {client.created_at && <span className="flex items-center gap-1 text-xs text-gray-400"><Calendar className="w-3 h-3" />Desde {formatDate(client.created_at)}</span>}
            </div>
            {client.broker_name && <p className="text-xs text-gray-400 mt-1">Corretor: {client.broker_name}</p>}
            {client.referral && <p className="text-xs text-gray-400">Indicado por: {client.referral}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
          <button onClick={function() { navigate("/clientes"); }} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-testid="profile-back-btn">
            <ArrowLeft className="w-3.5 h-3.5" />Voltar
          </button>
          <button onClick={onAddConsortium} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#F26A4B] hover:bg-[#E05A3B] rounded-lg transition-colors" data-testid="profile-add-consortium-btn">
            <Plus className="w-3.5 h-3.5" /><Car className="w-3.5 h-3.5" />Consórcio
          </button>
          <button onClick={onAddPolicy} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-white bg-[#3B82F6] hover:bg-[#2563EB] rounded-lg transition-colors" data-testid="profile-add-policy-btn">
            <Plus className="w-3.5 h-3.5" /><Shield className="w-3.5 h-3.5" />Apólice
          </button>
          {isManager && (
            <button onClick={function() { resetState[1](function(v) { return !v; }); }}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors" data-testid="profile-reset-pwd-btn">
              <KeyRound className="w-3.5 h-3.5" />Resetar Senha
            </button>
          )}
        </div>
        {resetState[0] && (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl" data-testid="reset-password-panel">
            <p className="text-xs text-amber-800 font-medium mb-2">Nova senha para {client.name}:</p>
            <div className="flex gap-2">
              <input type="text" value={pwdState[0]} onChange={function(e) { pwdState[1](e.target.value); }}
                placeholder="Mínimo 6 caracteres" data-testid="reset-password-input"
                className="flex-1 px-3 py-2 bg-white border border-amber-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
              <button onClick={handleResetPassword} disabled={resetLoadingState[0] || pwdState[0].length < 6} data-testid="reset-password-confirm"
                className="px-3 py-2 text-xs font-medium rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1">
                {resetLoadingState[0] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}Confirmar
              </button>
            </div>
            {resetMsgState[0] && (
              <p className={"text-xs mt-2 " + (resetMsgState[0].ok ? "text-emerald-700" : "text-red-700")}>{resetMsgState[0].msg}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
